import { useNovel } from '@/context/novel-context';
import { mapCharacterDTOToDomain } from '@/features/characters/api/mappers';
import { CharacterDTO } from '@/features/characters/schemas';
import { mapLocationDTOToDomain } from '@/features/locations/api/mappers';
import { LocationDTO } from '@/features/locations/schemas';
import { mapOrganizationDTOToDomain } from '@/features/organizations/api/mappers';
import { OrganizationDTO } from '@/features/organizations/schemas';
import { useEntity, useNarrativeItem } from '@/hooks/use-entity';
import { AnyEntity, ENTITY_TYPES, EnrichedEntity, EntityEventChange, EntityType, EventSummary, NARRATIVE_TYPES, NarrativeType, Scene } from '@/lib/types';
import { applyDeltas } from '@/lib/utils/apply-deltas';
import { useMemo } from 'react';

interface UseEditorDataProps {
  selectedEntitySummary: EnrichedEntity | null;
  activeSceneId?: string;
}


export function useEditorData({ selectedEntitySummary, activeSceneId }: UseEditorDataProps) {
  const { novel } = useNovel();

  const type = selectedEntitySummary?.entityType;
  const id = selectedEntitySummary?.id;

  const isWorldbuilding = type && (ENTITY_TYPES as readonly string[]).includes(type);
  const isNarrative = type && (NARRATIVE_TYPES as readonly string[]).includes(type);

  // Use polymorphic hooks that respect storageLayout
  const { data: worldbuildingData } = useEntity<AnyEntity>(
    isWorldbuilding ? type as EntityType : 'characters',
    isWorldbuilding ? id : undefined
  );

  const { data: narrativeData } = useNarrativeItem(
    isNarrative ? type as NarrativeType : 'scenes',
    isNarrative ? id : undefined
  );

  const { data: activeSceneData } = useNarrativeItem<Scene>('scenes', activeSceneId);

  const fullEntityData = isWorldbuilding ? worldbuildingData : isNarrative ? narrativeData : null;



  // Scenes are narrative items, we use the merged summaries
  const scenes = useMemo(() => {
    const sceneSummaries = Object.values(novel.summaries.scenes);
    return [...sceneSummaries].sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [novel]);

  const chapters = useMemo(() => {
    const chapterSummaries = Object.values(novel.summaries.chapters);
    return [...chapterSummaries].sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [novel]);

  // Enrichment (Injecting entityType for uniform handling in UI)
  const enrichedCharacters = useMemo<EnrichedEntity[]>(() =>
    Object.values(novel.summaries.characters).map(s => ({
      ...s,
      entityType: 'characters' as const
    }))
    , [novel]);

  const enrichedLocations = useMemo<EnrichedEntity[]>(() =>
    Object.values(novel.summaries.locations).map(s => ({
      ...s,
      entityType: 'locations' as const
    }))
    , [novel]);

  const enrichedOrganizations = useMemo<EnrichedEntity[]>(() =>
    Object.values(novel.summaries.organizations).map(s => ({
      ...s,
      entityType: 'organizations' as const
    }))
    , [novel]);

  type EnrichedEventItem = EnrichedEntity & EventSummary;

  const enrichedEvents = useMemo<EnrichedEventItem[]>(() =>
    Object.values(novel.summaries.events).sort((a, b) => (a.order ?? 0) - (b.order ?? 0)).map(s => ({
      ...s,
      entityType: 'events' as const
    } as EnrichedEventItem))
    , [novel]);

  const enrichedArcs = useMemo<EnrichedEntity[]>(() =>
    Object.values(novel.summaries.arcs).sort((a, b) => (a.order || 0) - (b.order || 0)).map(s => ({
      ...s,
      entityType: 'arcs' as const
    }))
    , [novel]);

  const selectedEntity = useMemo((): EnrichedEntity | null => {
    if (!fullEntityData || !selectedEntitySummary) return selectedEntitySummary;
    const type = selectedEntitySummary.entityType;

    let enriched = (() => {
      if (type === 'characters') return { ...selectedEntitySummary, ...mapCharacterDTOToDomain(fullEntityData as CharacterDTO), entityType: 'characters' as const };
      if (type === 'locations') return { ...selectedEntitySummary, ...mapLocationDTOToDomain(fullEntityData as LocationDTO), entityType: 'locations' as const };
      if (type === 'organizations') return { ...selectedEntitySummary, ...mapOrganizationDTOToDomain(fullEntityData as OrganizationDTO), entityType: 'organizations' as const };
      return { ...selectedEntitySummary, ...fullEntityData, entityType: type };
    })();

    // Apply event changes based on timeline state
    let anchorEventOrder = -1;

    if (activeSceneData?.eventIds && activeSceneData.eventIds.length > 0) {
      const activeSceneEvents = activeSceneData.eventIds
        .flatMap(id => {
          const event = enrichedEvents.find(e => e.id === id);
          return event ? [event] : [];
        });

      if (activeSceneEvents.length > 0) {
        const minOrder = Math.min(...activeSceneEvents.map(e => e.order ?? 0));
        // We want events that are strictly BEFORE the earliest event of the scene
        const priorEvents = enrichedEvents.filter(e => (e.order ?? 0) < minOrder);
        anchorEventOrder = -1;
        if (priorEvents.length > 0) {
          anchorEventOrder = priorEvents[priorEvents.length - 1].order ?? 0;
          // Assume the initial state is the event exactly 1 order prior
          // anchorEventOrder = minOrder - 1;
        }
      }
    } else if (activeSceneData) {
      // If the scene has no events, search for events in the prior scenes
      let foundAnchor = false;
      const currentChapterId = activeSceneData.chapterId;
      const currentChapterIndex = chapters.findIndex(c => c.id === currentChapterId);
      const sameChapterScenes = scenes.filter(s => s.chapterId === currentChapterId);
      const currentSceneIndex = sameChapterScenes.findIndex(s => s.id === activeSceneData.id);

      // 1. Check prior scenes in the same chapter
      for (let i = currentSceneIndex - 1; i >= 0; i--) {
        const prevScene = sameChapterScenes[i];
        if (prevScene.eventIds && prevScene.eventIds.length > 0) {
          const anchorEventId = prevScene.eventIds[prevScene.eventIds.length - 1];
          const eventSummary = enrichedEvents.find(e => e.id === anchorEventId);
          anchorEventOrder = eventSummary?.order ?? 0;
          foundAnchor = true;
          break;
        }
      }

      // 2. Check scenes in prior chapters (going backwards)
      if (!foundAnchor) {
        for (let i = currentChapterIndex - 1; i >= 0; i--) {
          const prevChapter = chapters[i];
          const chapterScenes = scenes
            .filter(s => s.chapterId === prevChapter.id)
            .sort((a, b) => (b.order || 0) - (a.order || 0)); // Descending

          for (const sSummary of chapterScenes) {
            if (sSummary.eventIds && sSummary.eventIds.length > 0) {
              const anchorEventId = sSummary.eventIds[sSummary.eventIds.length - 1];
              const eventSummary = enrichedEvents.find(e => e.id === anchorEventId);
              anchorEventOrder = eventSummary?.order ?? 0;
              foundAnchor = true;
              break;
            }
          }
          if (foundAnchor) break;
        }
      }
    }

    const changes = worldbuildingData?.eventChanges;
    if (changes && Array.isArray(changes) && anchorEventOrder >= 0) {
      const relevantChanges = changes
        .map((ec) => {
          const change = ec as EntityEventChange;
          const eventSummary = enrichedEvents.find(e => e.id === change.eventId);
          return { ...change, _resolvedOrder: eventSummary?.order ?? -1 };
        })
        .filter((ec) => ec._resolvedOrder >= 0 && ec._resolvedOrder <= anchorEventOrder)
        .sort((a, b) => a._resolvedOrder - b._resolvedOrder);

      for (const ec of relevantChanges) {
        enriched = applyDeltas(enriched, ec.changes);
      }
    }

    return enriched;
  }, [fullEntityData, selectedEntitySummary, activeSceneData?.eventIds, enrichedEvents]);


  // Fallback to summary if full data is not yet loaded
  const activeScene = useMemo(() => {
    if (!activeSceneId) return activeSceneData;
    const summary = novel.summaries.scenes[activeSceneId] ||
      Object.values(novel.summaries.scenes).find(s => s.mainContentId === activeSceneId || s.contentVariants?.includes(activeSceneId) || s.id === activeSceneId);
    if (!summary) return activeSceneData;
    return { ...summary, ...activeSceneData };
  }, [activeSceneId, novel, activeSceneData]);

  return {
    novel,
    scenes,
    chapters,
    enrichedCharacters,
    enrichedLocations,
    enrichedOrganizations,
    enrichedEvents,
    enrichedArcs,
    selectedEntity,
    activeScene,
  };
}

